import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTour, createReservation } from '../services/api';
import {
  Star, ChevronLeft, ChevronRight, MapPin, Clock, Users, Mountain,
  Check, X as XIcon, Calendar, ArrowLeft, Heart, Share2, Instagram,
  CalendarDays, Phone, Mail, User as UserIcon, MessageSquare, Info, Hotel,
} from 'lucide-react';
import SEO from './SEO';
import RelatedContent from './RelatedContent';
import WishlistButton from './WishlistButton';
import { imgErrorFallback } from '../utils/imageFallback';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useLockFloatingButtons } from '../hooks/useModalOpen';
import { validateReservationForm, focusFirstError } from '../utils/formValidation';
import { parseTurkishDateRange } from '../utils/dateParser';
import { trackInitiateCheckout, trackAddToCart } from '../utils/analytics';

const sections = [
  { key: 'overview', label: 'Genel Bakış' },
  { key: 'dates', label: 'Tarihler' },
  { key: 'itinerary', label: 'Program' },
  { key: 'included', label: 'Dahil Olanlar' },
  { key: 'reviews', label: 'Değerlendirmeler' },
];

const formatPrice = (price) => new Intl.NumberFormat('tr-TR').format(price);

/* ─── Reservation Modal ─── */
const ReservationModal = ({ tour, selectedDateRow, onClose }) => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    participants: 1, roomType: 'double', notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  useLockFloatingButtons();

  const fieldCls = (field, extra = '') =>
    `w-full border rounded-lg py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent ${extra} ${
      fieldErrors[field]
        ? 'border-red-400 focus:ring-red-300'
        : 'border-gray-200 focus:ring-[#C8D843]'
    }`;

  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (fieldErrors[field]) setFieldErrors((p) => ({ ...p, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    const errors = validateReservationForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Lütfen işaretli alanları kontrol edin.');
      focusFirstError(errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      await createReservation({
        tourId: tour.id,
        tourTitle: tour.title,
        selectedDate: selectedDateRow.date,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        participants: Number(form.participants) || 1,
        roomType: form.roomType,
        notes: form.notes || '',
      });
      trackAddToCart({
        content_name: tour.title,
        content_category: 'tour',
        content_ids: [tour.id],
        value: (selectedDateRow.perPerson || 0) * (Number(form.participants) || 1),
        currency: 'TRY',
        selected_date: selectedDateRow.date,
        num_items: Number(form.participants) || 1,
        room_type: form.roomType,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Rezervasyon kaydedilemedi. Lütfen tekrar deneyin.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl p-7 md:p-8 max-w-md w-full text-center" onClick={(e) => e.stopPropagation()} data-testid="reservation-success">
          <div className="w-16 h-16 bg-[#C8D843]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-[#3D4A26]" />
          </div>
          <h3 className="text-xl font-bold text-[#1B1B1B] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Ön rezervasyonunuz bize ulaştı
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            Yerinizi geçici olarak ayırdık. Rezervasyonunuzun kesinleşmesi için kişi başı
            <strong> 4.000₺</strong> ön ödeme yapmanız gerekiyor
            {Number(form.participants) > 1 && (
              <> ({form.participants} kişi için toplam <strong>{new Intl.NumberFormat('tr-TR').format(Number(form.participants) * 4000)}₺</strong>)</>
            )}
            . Ödemeniz bize ulaştıktan sonra ekibimiz sizinle iletişime geçecek.
          </p>
          <div className="bg-[#f0f4d8] border border-[#C8D843]/40 rounded-xl p-4 text-left mb-5">
            <p className="text-xs font-bold text-[#3D4A26] uppercase tracking-wider mb-1.5">Ödeme bilgileri e-postanızda</p>
            <p className="text-xs text-[#3D4A26]/90 leading-relaxed break-words">
              Rezervasyon özetiniz ve banka hesap bilgilerimiz <strong>{form.email}</strong> adresine
              gönderildi. E-postayı göremezseniz lütfen spam veya gereksiz klasörünü kontrol edin.
            </p>
          </div>
          <p className="text-xs text-gray-500 mb-5">
            {tour.title} • {selectedDateRow.date}
          </p>
          <button onClick={onClose} data-testid="reservation-success-close" className="w-full bg-[#C8D843] text-[#1B1B1B] font-bold py-3 rounded-full hover:bg-[#b8c83a] transition-colors">
            Tamam
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[92dvh] overflow-y-auto overscroll-contain" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1B1B1B]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Ön Rezervasyon Formu
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <XIcon size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Info Banner */}
        <div className="mx-6 mt-4 bg-[#f0f4d8] border border-[#C8D843]/30 rounded-xl p-4 flex items-start gap-3">
          <Info size={18} className="text-[#3D4A26] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[#3D4A26]">Bu aşamada ödeme alınmayacaktır</p>
            <p className="text-xs text-[#3D4A26]/80 mt-0.5">
              Sadece ön rezervasyon oluşturuyorsunuz. Ekibimiz sizinle iletişime geçerek detayları paylaşacaktır.
            </p>
          </div>
        </div>

        {/* Selected Date Info */}
        <div className="mx-6 mt-4 bg-[#f9f9f6] rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Seçilen Tur</p>
          <p className="font-semibold text-sm text-[#1B1B1B]">{tour.title}</p>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-xs text-gray-600"><CalendarDays size={12} className="inline mr-1" />{selectedDateRow.date}</span>
            <span className="text-xs font-semibold text-[#1B1B1B]">₺{formatPrice(selectedDateRow.perPerson)} / kişi</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Ad *</label>
              <div className="relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  required type="text" value={form.firstName}
                  data-field="firstName"
                  onChange={update('firstName')}
                  className={fieldCls('firstName', 'pl-9 pr-3')}
                  placeholder="Adınız"
                />
              </div>
              {fieldErrors.firstName && <p className="text-xs text-red-600 mt-1">{fieldErrors.firstName}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Soyad *</label>
              <input
                required type="text" value={form.lastName}
                data-field="lastName"
                onChange={update('lastName')}
                className={fieldCls('lastName', 'px-3')}
                placeholder="Soyadınız"
              />
              {fieldErrors.lastName && <p className="text-xs text-red-600 mt-1">{fieldErrors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">E-posta *</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                required type="email" value={form.email}
                data-field="email"
                onChange={update('email')}
                className={fieldCls('email', 'pl-9 pr-3')}
                placeholder="ornek@email.com"
              />
            </div>
            {fieldErrors.email && <p className="text-xs text-red-600 mt-1">{fieldErrors.email}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Telefon *</label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                required type="tel" value={form.phone}
                data-field="phone"
                inputMode="tel"
                onChange={update('phone')}
                className={fieldCls('phone', 'pl-9 pr-3')}
                placeholder="05XX XXX XX XX"
              />
            </div>
            {fieldErrors.phone && <p className="text-xs text-red-600 mt-1">{fieldErrors.phone}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Katılımcı Sayısı</label>
              <select
                value={form.participants}
                onChange={(e) => setForm({...form, participants: Number(e.target.value)})}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] focus:border-transparent bg-white"
              >
                {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} kişi</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Oda Tipi</label>
              <select
                value={form.roomType}
                onChange={(e) => setForm({...form, roomType: e.target.value})}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] focus:border-transparent bg-white"
              >
                <option value="double">İki Kişilik Oda</option>
                <option value="single">Tek Kişilik Oda</option>
                <option value="extra">İlave Yatak</option>
              </select>
            </div>
          </div>

          {form.roomType === 'single' && (
            <div
              role="note"
              data-testid="single-room-notice"
              className="flex items-start gap-2 bg-[#f6efe3] border border-[#B8734A]/30 text-[#1B1B1B] rounded-lg px-3 py-2.5 text-xs sm:text-sm"
            >
              <Info size={16} className="text-[#B8734A] flex-shrink-0 mt-0.5" />
              <p className="leading-snug">
                <span className="font-semibold">Tek Kişilik Oda</span> seçtiniz.
                Single farkı <span className="font-semibold">gecelik 1.500 ₺</span>&apos;dir ve toplam
                ücrete eklenecektir.
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Notlarınız</label>
            <div className="relative">
              <MessageSquare size={16} className="absolute left-3 top-3 text-gray-400" />
              <textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] focus:border-transparent resize-none"
                rows={3} placeholder="Varsa özel isteklerinizi belirtiniz..."
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="reservation-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="reservation-submit-button"
            className="w-full bg-[#C8D843] text-[#1B1B1B] font-bold py-3.5 rounded-full text-base hover:bg-[#b8c83a] transition-all duration-300 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Gönderiliyor...' : 'Ön Rezervasyon Oluştur'}
          </button>

          <p className="text-xs text-gray-400 text-center">
            Bu aşamada herhangi bir ödeme alınmamaktadır.
          </p>
        </form>
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const TourDetail = () => {
  const { slug } = useParams();
  const siteSettings = useSiteSettings();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [activeImage, setActiveImage] = useState(0);
  const [expandedDay, setExpandedDay] = useState(null);
  const [reservationModal, setReservationModal] = useState(null);
  const [cancellationPolicy, setCancellationPolicy] = useState('');
  const sectionRefs = useRef({});
  const tabBarRef = useRef(null);

  const upcomingDates = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (tour?.pricingDates || []).filter((row) => {
      const parsed = parseTurkishDateRange(row.date || '');
      const last = parsed?.endDate || parsed?.startDate;
      return !last || last >= today;
    });
  }, [tour]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchData = async () => {
      try {
        const [tourRes, settingsRes] = await Promise.all([
          getTour(slug),
          import('../services/api').then(m => m.getSettings())
        ]);
        setTour(tourRes.data);
        setCancellationPolicy(settingsRes.data?.cancellationPolicy || '');
      } catch (err) {
        console.error('Tour fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Scroll spy
  const handleScroll = useCallback(() => {
    const offset = 200;
    for (let i = sections.length - 1; i >= 0; i--) {
      const el = sectionRefs.current[sections[i].key];
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= offset) {
          setActiveSection(sections[i].key);
          break;
        }
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (key) => {
    const el = sectionRefs.current[key];
    if (el) {
      const yOffset = -160;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-gray-500">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#1B1B1B] mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>Tur bulunamadı</h2>
          <Link to="/" className="text-[#3D4A26] font-semibold underline">Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  const SITE_URL = 'https://camption.com';
  const canonical = `${SITE_URL}/tur/${tour.slug}`;
  const metaTitle = tour.metaTitle || tour.title;
  const metaDescription = tour.metaDescription || (tour.shortDescription || tour.description || '').slice(0, 160);
  const ogImage = tour.heroImage || (tour.images && tour.images[0]);
  const faqItems = Array.isArray(tour.faqItems) ? tour.faqItems.filter((f) => f.question && f.answer) : [];

  const graph = [
    {
      '@type': 'TouristTrip',
      '@id': `${canonical}#trip`,
      name: tour.title,
      description: metaDescription,
      image: ogImage ? [ogImage] : undefined,
      touristType: tour.levelName || 'Adventure Traveler',
      inLanguage: 'tr-TR',
      itinerary: {
        '@type': 'ItemList',
        itemListElement: (tour.itinerary || []).map((d, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `Gün ${d.day}: ${d.title}`,
          description: d.description,
        })),
      },
      offers: {
        '@type': 'Offer',
        price: tour.salePrice,
        priceCurrency: 'TRY',
        availability: 'https://schema.org/InStock',
        url: canonical,
      },
      aggregateRating:
        tour.rating && tour.reviewCount
          ? {
              '@type': 'AggregateRating',
              ratingValue: tour.rating,
              reviewCount: tour.reviewCount,
            }
          : undefined,
      provider: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'TravelAgency',
      '@id': `${SITE_URL}/#organization`,
      name: 'Camption',
      url: SITE_URL,
      email: 'hello@camption.com',
      telephone: '+908504959524',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Andifli, Nevzat Hacıoğlu Sokak No:3',
        addressLocality: 'Kaş',
        addressRegion: 'Antalya',
        postalCode: '07580',
        addressCountry: 'TR',
      },
      areaServed: 'Likya Yolu, Karya Yolu',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Turlar', item: `${SITE_URL}/turlar` },
        { '@type': 'ListItem', position: 3, name: tour.title },
      ],
    },
  ];

  if (faqItems.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: faqItems.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  const tourJsonLd = { '@context': 'https://schema.org', '@graph': graph };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={metaTitle}
        description={metaDescription}
        image={ogImage}
        url={canonical}
        type="article"
        jsonLd={tourJsonLd}
      />
      {/* Hero Image Gallery */}
      <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden bg-gray-900">
        {/* Blurred background for better composition */}
        <div className="absolute inset-0 hidden lg:block">
          <img 
            src={tour.images[activeImage]} 
            alt="" 
            onError={imgErrorFallback}
            className="w-full h-full object-cover blur-2xl opacity-40 scale-110" 
          />
        </div>
        {/* Main image - contain on desktop, cover on mobile */}
        <img 
          src={tour.images[activeImage]} 
          alt={tour.title} 
          onError={imgErrorFallback}
          className="relative w-full h-full object-contain lg:object-contain object-cover transition-all duration-500" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
        <Link to="/" className="absolute top-5 left-5 z-20 bg-white/20 backdrop-blur-sm text-white rounded-full p-2.5 hover:bg-white/30 transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div className="absolute top-5 right-5 z-20 flex items-center gap-2">
          <WishlistButton tourId={tour.id} tourTitle={tour.title} variant="hero" />
          <button className="bg-white/20 backdrop-blur-sm text-white rounded-full p-2.5 hover:bg-white/30 transition-colors"><Share2 size={18} /></button>
        </div>
        {tour.images.length > 1 && (
          <>
            <button onClick={() => setActiveImage((p) => (p === 0 ? tour.images.length - 1 : p - 1))} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors"><ChevronLeft size={22} className="text-white" /></button>
            <button onClick={() => setActiveImage((p) => (p === tour.images.length - 1 ? 0 : p + 1))} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors"><ChevronRight size={22} className="text-white" /></button>
          </>
        )}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {tour.images.map((_, idx) => (
            <button key={idx} onClick={() => setActiveImage(idx)} className={`w-2.5 h-2.5 rounded-full transition-all ${idx === activeImage ? 'bg-white w-6' : 'bg-white/50'}`} />
          ))}
        </div>
        <div className="absolute bottom-5 left-5 z-20">
          <span className="bg-[#C8D843] text-[#1B1B1B] text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">{tour.category}</span>
        </div>
      </div>

      {/* Thumbnail strip */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {tour.images.map((img, idx) => (
            <button key={idx} onClick={() => setActiveImage(idx)} className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === activeImage ? 'border-[#C8D843]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
              <img src={img} alt="" onError={imgErrorFallback} className="w-full h-full object-cover object-center" />
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left: Main content */}
          <div className="lg:col-span-2">
            {/* Title & Rating */}
            <h1 className="text-3xl md:text-4xl font-bold text-[#1B1B1B] mb-3" style={{ fontFamily: 'DM Sans, sans-serif' }}>{tour.title}</h1>
            <div className="flex items-center flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((s) => <Star key={s} size={16} fill={s <= Math.round(tour.rating) ? '#C8D843' : '#e5e7eb'} stroke={s <= Math.round(tour.rating) ? '#C8D843' : '#e5e7eb'} />)}
                <span className="ml-1 text-sm font-semibold">{tour.rating}</span>
                <span className="text-sm text-gray-500">({tour.reviewCount} değerlendirme)</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-600"><MapPin size={14} /><span>{tour.location}</span></div>
            </div>

            {/* Quick Info Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 bg-[#f9f9f6] rounded-xl mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C8D843]/15 rounded-lg flex items-center justify-center"><Clock size={18} className="text-[#3D4A26]" /></div>
                <div><p className="text-xs text-gray-500">Süre</p><p className="text-sm font-semibold text-[#1B1B1B]">{tour.duration}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C8D843]/15 rounded-lg flex items-center justify-center"><Mountain size={18} className="text-[#3D4A26]" /></div>
                <div><p className="text-xs text-gray-500">Zorluk</p><p className="text-sm font-semibold text-[#1B1B1B]">Seviye {tour.level} - {tour.levelName}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C8D843]/15 rounded-lg flex items-center justify-center"><Users size={18} className="text-[#3D4A26]" /></div>
                <div><p className="text-xs text-gray-500">Grup</p><p className="text-sm font-semibold text-[#1B1B1B]">Maks. {tour.groupSize} kişi</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C8D843]/15 rounded-lg flex items-center justify-center"><Hotel size={18} className="text-[#3D4A26]" /></div>
                <div><p className="text-xs text-gray-500">Konaklama</p><p className="text-sm font-semibold text-[#1B1B1B]">Otel / Bungalov</p></div>
              </div>
            </div>

            {/* Sticky Tab Bar */}
            <div ref={tabBarRef} className="sticky top-[72px] z-30 bg-white border-b border-gray-200 mb-8 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
              <div className="flex items-center gap-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {sections.map((sec) => (
                  <button
                    key={sec.key}
                    onClick={() => scrollToSection(sec.key)}
                    className={`pb-3 pt-3 text-sm font-semibold transition-colors relative whitespace-nowrap ${
                      activeSection === sec.key ? 'text-[#1B1B1B]' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {sec.label}
                    {sec.key === 'reviews' && ` (${tour.reviewCount})`}
                    {activeSection === sec.key && (
                      <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#C8D843] rounded-t-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Section: Overview ── */}
            <div ref={(el) => (sectionRefs.current.overview = el)} id="section-overview" className="mb-14">
              <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-6 line-clamp-6 md:line-clamp-none whitespace-pre-line">
                {tour.description}
              </p>
              {tour.highlights && tour.highlights.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-base md:text-lg font-bold text-[#1B1B1B] mb-3" style={{ fontFamily: 'DM Sans, sans-serif' }}>Öne Çıkan Özellikler</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {tour.highlights.map((h, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 md:w-5 md:h-5 mt-0.5 bg-[#C8D843] rounded-full flex items-center justify-center flex-shrink-0"><Check size={10} className="text-[#1B1B1B] md:w-3 md:h-3" /></div>
                        <span className="text-xs md:text-sm text-gray-700">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Section: Dates & Pricing ── */}
            <div ref={(el) => (sectionRefs.current.dates = el)} id="section-dates" className="mb-14">
              <h2 className="text-2xl font-bold text-[#1B1B1B] mb-2 flex items-center gap-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                <CalendarDays size={24} className="text-[#3D4A26]" />
                Tarihler ve Fiyatlandırma
              </h2>
              <p className="text-sm text-gray-500 mb-6">Fiyatlar Türk Lirası (₺) olarak belirtilmiştir.</p>

              {/* Pricing Cards — one card per date, "Ön Rezervasyon" button always visible */}
              <div className="space-y-3" data-testid="dates-list">
                {upcomingDates.map((row, idx) => (
                  <div
                    key={idx}
                    data-testid={`date-card-${idx}`}
                    className="border border-gray-200 rounded-xl bg-white hover:border-[#3D4A26]/40 hover:shadow-sm transition-all p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-base font-bold text-[#1B1B1B]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                        {row.date}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        ₺{formatPrice(row.perPerson)} / kişi · iki kişilik oda
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        trackInitiateCheckout({
                          content_name: tour.title,
                          content_category: 'tour',
                          content_ids: [tour.id],
                          value: row.perPerson || 0,
                          currency: 'TRY',
                          selected_date: row.date,
                        });
                        setReservationModal(row);
                      }}
                      data-testid={`date-card-book-${idx}`}
                      className="bg-[#3D4A26] text-white font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-all duration-200 hover:shadow-md w-full sm:w-auto flex-shrink-0"
                    >
                      Ön Rezervasyon
                    </button>
                  </div>
                ))}
                {upcomingDates.length === 0 && (
                  <div
                    className="border border-dashed border-gray-300 rounded-xl p-5 text-center"
                    data-testid="dates-empty"
                  >
                    <p className="text-sm font-semibold text-[#1B1B1B] mb-1">Bu tur için planlı tarih kalmadı</p>
                    <p className="text-xs text-gray-500 mb-4">
                      Yeni tarihler yakında açılacak. Size uygun bir tarih için bizimle iletişime geçebilirsiniz.
                    </p>
                    <Link
                      to="/grup-rezervasyonu"
                      className="inline-flex items-center gap-2 bg-[#3D4A26] text-white font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors"
                    >
                      Tarih Talep Edin
                    </Link>
                  </div>
                )}
              </div>

              {/* No payment note */}
              <div className="mt-4 bg-[#f0f4d8] border border-[#C8D843]/30 rounded-xl p-4 flex items-start gap-3">
                <Info size={18} className="text-[#3D4A26] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#3D4A26]">
                  <span className="font-semibold">Bu aşamada ödeme alınmayacaktır.</span> Tarih seçtikten sonra ön rezervasyon formunu doldurmanız yeterlidir.
                  Ekibimiz sizinle iletişime geçerek detayları ve ödeme bilgilerini paylaşacaktır.
                </p>
              </div>
            </div>

            {/* ── Section: Itinerary ── */}
            <div ref={(el) => (sectionRefs.current.itinerary = el)} id="section-itinerary" className="mb-14">
              <h2 className="text-2xl font-bold text-[#1B1B1B] mb-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>Program</h2>
              <div className="space-y-4">
                {tour.itinerary.map((day) => (
                  <div key={day.day} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button onClick={() => setExpandedDay(expandedDay === day.day ? null : day.day)} className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors text-left">
                      <div className="flex items-center gap-4">
                        <span className="w-10 h-10 bg-[#C8D843] rounded-full flex items-center justify-center text-sm font-bold text-[#1B1B1B]">{day.day}</span>
                        <div>
                          <p className="font-bold text-[#1B1B1B] text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>Gün {day.day}</p>
                          <p className="text-sm text-gray-600">{day.title}</p>
                        </div>
                      </div>
                      <ChevronRight size={18} className={`text-gray-400 transition-transform ${expandedDay === day.day ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedDay === day.day && (
                      <div className="px-5 pb-5 border-t border-gray-100">
                        <p className="text-sm text-gray-700 leading-relaxed mt-4 mb-4">{day.description}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-[#f9f9f6] rounded-lg p-3"><p className="text-xs text-gray-500">Mesafe</p><p className="text-sm font-semibold">{day.distance}</p></div>
                          <div className="bg-[#f9f9f6] rounded-lg p-3"><p className="text-xs text-gray-500">İrtifa</p><p className="text-sm font-semibold">{day.elevation}</p></div>
                          <div className="bg-[#f9f9f6] rounded-lg p-3"><p className="text-xs text-gray-500">Zorluk</p><p className="text-sm font-semibold">{day.difficulty}</p></div>
                          <div className="bg-[#f9f9f6] rounded-lg p-3"><p className="text-xs text-gray-500">Öğünler</p><p className="text-sm font-semibold">{day.meals.join(', ')}</p></div>
                        </div>
                        {day.accommodation && <p className="mt-3 text-sm text-gray-600"><span className="font-medium">Konaklama:</span> {day.accommodation}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Section: Included ── */}
            <div ref={(el) => (sectionRefs.current.included = el)} id="section-included" className="mb-14">
              <h2 className="text-2xl font-bold text-[#1B1B1B] mb-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>Dahil Olanlar</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-base font-bold text-[#1B1B1B] mb-4">Dahil Olan Hizmetler</h3>
                  <div className="space-y-3">
                    {tour.included.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 mt-0.5 bg-[#C8D843] rounded-full flex items-center justify-center flex-shrink-0"><Check size={12} className="text-[#1B1B1B]" /></div>
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1B1B1B] mb-4">Dahil Olmayan Hizmetler</h3>
                  <div className="space-y-3">
                    {tour.notIncluded.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 mt-0.5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0"><XIcon size={12} className="text-red-500" /></div>
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Section: Reviews ── */}
            <div ref={(el) => (sectionRefs.current.reviews = el)} id="section-reviews" className="mb-14">
              <h2 className="text-2xl font-bold text-[#1B1B1B] mb-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>Değerlendirmeler</h2>
              <div className="flex items-center gap-6 mb-8 p-6 bg-[#f9f9f6] rounded-xl">
                <div className="text-center">
                  <p className="text-4xl font-bold text-[#1B1B1B]">{tour.rating}</p>
                  <div className="flex items-center gap-0.5 mt-1">
                    {[1,2,3,4,5].map((s) => <Star key={s} size={14} fill={s <= Math.round(tour.rating) ? '#C8D843' : '#e5e7eb'} stroke={s <= Math.round(tour.rating) ? '#C8D843' : '#e5e7eb'} />)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{tour.reviewCount} değerlendirme</p>
                </div>
              </div>
              <div className="space-y-6">
                {tour.reviews.map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#3D4A26] rounded-full flex items-center justify-center"><span className="text-white font-bold text-sm">{review.name.charAt(0)}</span></div>
                        <div><p className="font-semibold text-sm text-[#1B1B1B]">{review.name}</p><p className="text-xs text-gray-500">{review.date}</p></div>
                      </div>
                      <div className="flex items-center gap-0.5">{[1,2,3,4,5].map((s) => <Star key={s} size={12} fill={s <= review.rating ? '#C8D843' : '#e5e7eb'} stroke={s <= review.rating ? '#C8D843' : '#e5e7eb'} />)}</div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Section: FAQ (AEO) ── */}
            {faqItems.length > 0 && (
              <div className="mb-14" data-testid="tour-faq-section">
                <h2 className="text-2xl font-bold text-[#1B1B1B] mb-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>Sıkça Sorulan Sorular</h2>
                <div className="space-y-3">
                  {faqItems.map((f, i) => (
                    <details
                      key={i}
                      className="group border border-gray-200 rounded-xl bg-white hover:border-[#3D4A26]/40 transition-colors"
                      data-testid={`tour-faq-${i}`}
                    >
                      <summary className="cursor-pointer flex items-start justify-between gap-4 p-5 list-none">
                        <span className="font-semibold text-[#1B1B1B]">{f.question}</span>
                        <ChevronRight size={18} className="text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 mt-0.5" />
                      </summary>
                      <div className="px-5 pb-5 -mt-1 text-sm text-gray-700 leading-relaxed whitespace-pre-line">{f.answer}</div>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <div className="mb-5">
                  <p className="text-xs text-gray-500 mb-1">Kişi başı fiyat</p>
                  <div className="flex items-center gap-2">
                    {tour.onSale && tour.originalPrice && <span className="text-lg text-gray-400 line-through">{tour.currency}{formatPrice(tour.originalPrice)}</span>}
                    <span className="text-3xl font-bold text-[#1B1B1B]">{tour.currency}{formatPrice(tour.salePrice)}</span>
                  </div>
                  {tour.onSale && <span className="inline-block mt-1 bg-red-50 text-red-600 text-xs font-semibold px-2 py-0.5 rounded">İndirimli</span>}
                </div>

                <button onClick={() => scrollToSection('dates')} className="w-full bg-[#C8D843] text-[#1B1B1B] font-bold py-3.5 rounded-full text-base hover:bg-[#b8c83a] transition-all duration-300 hover:shadow-lg mb-3">
                  Tarih Seç & Rezervasyon
                </button>
                <p className="text-xs text-gray-500 text-center mb-5">Bu aşamada ödeme alınmayacaktır</p>

                <div className="border-t border-gray-100 pt-5 space-y-3">
                  <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Süre</span><span className="font-medium">{tour.duration}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Zorluk</span><span className="font-medium">Seviye {tour.level}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Grup</span><span className="font-medium">Maks. {tour.groupSize} kişi</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Konum</span><span className="font-medium">{tour.location}</span></div>
                </div>

                <div className="mt-5 pt-5 border-t border-gray-100">
                  <p className="text-sm font-semibold text-[#1B1B1B] mb-2">Sorularınız mı var?</p>
                  <a href={`tel:${(siteSettings.phone || '').replace(/\D/g, '')}`} className="flex items-center justify-center gap-2 w-full border-2 border-[#1B1B1B] text-[#1B1B1B] font-semibold py-2.5 rounded-full text-sm hover:bg-[#1B1B1B] hover:text-white transition-all duration-300">
                    {siteSettings.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related: blogs + tours */}
        <RelatedContent
          excludeSlug={tour.slug}
          matchKeyphrase={tour.focusKeyphrase || tour.location || tour.title}
          heading="Sıradaki Adım"
          blogLimit={3}
          tourLimit={3}
        />
      </div>

      {/* Reservation Modal */}
      {reservationModal && (
        <ReservationModal
          tour={tour}
          selectedDateRow={reservationModal}
          onClose={() => setReservationModal(null)}
        />
      )}
    </div>
  );
};

export default TourDetail;
